import { DeviceTypeIds, NwkRoute, RE4, RE4Read } from '@ncss/models/dist/v3';
import { BLE_SERVICES, DirectConnectBaseDevice, VisibleDevice } from '../base/directConnectBaseDevice';
import { ByteList } from 'byte-list';
import { BitwiseHelper } from '@ncss/message';
import { RE4BLEDeviceMessage, RE4BLEMessageType } from './messages/RE4BLEDeviceMessage';
import { ref, shallowRef, ShallowRef } from 'vue';
import { RE4BLEInfoMessage, RE4BLEInfoMessageType } from './messages/RE4BLEInfoMessage';
import { DeviceRouteMessage, RE4DeviceRouteMsgType } from './messages/RE4BLERouteMessage';
import { DeviceRouteRequestMessage, RE4DeviceRouteRequestMsgType } from './messages/RE4BLERouteRequestMessage';

export enum RE4InputVoltage {
  FIFTEEN_V = '15v',
  TWELVE_V = '12v',
  UNKNOWN_V = 'Unknown',
}

export class DirectConnectRE4 extends DirectConnectBaseDevice {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.RE4, DirectConnectRE4);
  }

  temperature?: number;
  get powerPinState() {
    return this.info?.powerPinState;
  }
  get rawRfSignal() {
    return BitwiseHelper.GetBits(this.flags || 0, 1, 3);
  }
  get linkQualityStr() {
    return this._linkQualityStr.value;
  }
  get loading() {
    return this._loading.value;
  }
  get info() {
    return this._info.value;
  }
  get read() {
    return this._read.value;
  }
  get pullingRouteTable() {
    return !!this._pullingRouteTable.value;
  }
  get routeCount() {
    return this._routeCount.value;
  }

  protected serviceId = BLE_SERVICES.WALK_BY.ID;
  protected notifyCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.WRITE;

  private _pullingRouteTable = ref(false);
  private _routeCount = ref(0);
  private flags?: number;
  private _loading: ShallowRef<boolean>;
  private _info: ShallowRef<RE4 | undefined>;
  private _read: ShallowRef<RE4Read | undefined>;
  private _linkQualityStr: ShallowRef<string | undefined>;
  private _requestInfoInterval?: NodeJS.Timeout;

  constructor(visibleDevice: VisibleDevice, useMetric: boolean) {
    super(visibleDevice, useMetric);
    this._loading = shallowRef(false);
    this._info = shallowRef();
    this._read = shallowRef();
    this._linkQualityStr = shallowRef();
  }

  protected parseManufacturerData(bytes: ByteList): void {
    this.temperature = bytes.readInt8();
    this.flags = bytes.readByte();
  }

  protected registerMessages(): void {
    this.messageService.registerMessageGroup({
      mask: RE4BLEMessageType.GROUP_MASK,
      messageClass: RE4BLEDeviceMessage,
      handler: this.onDeviceMessage.bind(this),
    });
    this.messageService.registerMessageGroup({
      mask: RE4DeviceRouteMsgType.RE4_DEVICE_ROUTE_MSG,
      messageClass: DeviceRouteMessage,
      handler: this.onRouteMessage.bind(this),
    });
  }

  protected onConnected() {
    super.onConnected();
    this._loading.value = true;
    clearInterval(this._requestInfoInterval);
    this._requestInfoInterval = setInterval(() => {
      this.requestInfo();
    }, 1000);
  }

  protected onDisconnected(): void {
    super.onDisconnected();
    clearInterval(this._requestInfoInterval);
  }

  setRapidCheckIn(enabled: boolean) {
    const msg = new RE4BLEInfoMessage(this.deviceId, RE4BLEInfoMessageType.INFO_MSG);
    if (enabled) {
      msg.re4Info.startRapidCheckIn = true;
    } else {
      msg.re4Info.stopRapidCheckIn = true;
    }
    return this.sendMessage(msg, {
      match: (res) => res instanceof RE4BLEDeviceMessage && res.re4?.rapidCheckInEnabled === enabled,
    }).then((res) => !!res);
  }

  async pullRouteTable() {
    const routes: NwkRoute[] = [];
    let page = 1;
    let keepGoing = true;
    this._routeCount.value = 0;
    this._pullingRouteTable.value = true;
    while (keepGoing) {
      const msg = new DeviceRouteRequestMessage();
      msg.deviceId = this.deviceId;
      msg.type = RE4DeviceRouteRequestMsgType.RE4_DEVICE_ROUTE_REQUEST;
      msg.startingPage = page;
      msg.flags = 0;
      const res = await this.sendMessage<DeviceRouteMessage>(msg, {
        match: (res) => res instanceof DeviceRouteMessage,
      });
      if (!res) {
        keepGoing = false;
      } else {
        if (res?.routes?.length) {
          routes.push(...res.routes);
          this._routeCount.value = routes.length;
          page++;
        } else {
          keepGoing = false;
        }
      }
    }
    this._pullingRouteTable.value = false;
    return routes;
  }

  async sortRouteTable(): Promise<boolean> {
    const msg = new DeviceRouteRequestMessage();
    msg.deviceId = this.deviceId;
    msg.type = RE4DeviceRouteRequestMsgType.RE4_DEVICE_ROUTE_REQUEST;
    msg.flags = 0;
    msg.sortRoutingTable = true;
    const res = await this.sendMessage<DeviceRouteMessage>(msg, {
      match: (res) => !!res && res instanceof DeviceRouteMessage && res.currentlySortingTable,
    });
    return !!res;
  }

  async dropRouteTable(): Promise<void> {
    const msg = new DeviceRouteRequestMessage();
    msg.deviceId = this.deviceId;
    msg.type = RE4DeviceRouteRequestMsgType.RE4_DEVICE_ROUTE_REQUEST;
    msg.flags = 0;
    msg.dropRoutingTable = true;
    await this.sendMessage<DeviceRouteMessage>(msg);
  }

  async toggleParty(): Promise<void> {
    const partyModeEnabled = !!this.info?.partyModeEnabled;
    const msg = new RE4BLEInfoMessage(this.deviceId, RE4BLEInfoMessageType.INFO_MSG);
    msg.re4Info.setPartyMode = true;
    msg.re4Info.startTheParty = !partyModeEnabled;
    await this.sendMessage(msg);
  }

  private requestInfo() {
    const msg = new RE4BLEInfoMessage(this.deviceId, RE4BLEInfoMessageType.REQUEST_INFO);
    return this.sendMessage<RE4BLEDeviceMessage>(msg, {
      match: (res) => !!res,
    });
  }

  private onDeviceMessage(msg: RE4BLEDeviceMessage) {
    this._loading.value = false;
    if (!msg.re4) {
      return;
    }
    this._read.value = RE4Read.Create(this.deviceId, msg.re4, {
      firstHopId: 0,
      firstHopRssi: 0,
      isProgrammed: false,
      lastHopRssi: 0,
    });
    this.setInfo(msg.re4);
    this.checkResponse(msg);
  }

  private onRouteMessage(msg: DeviceRouteMessage) {
    this.checkResponse(msg);
  }

  private setInfo(re4: RE4) {
    this._info.value = re4;
    this._linkQualityStr.value = this.computeLinkQualityStr(re4.rfSignal);
  }

  private computeLinkQualityStr(rfSignal: number) {
    switch (rfSignal) {
      case 4:
        return 'Excellent';
      case 3:
      case 2:
        return 'Good';
      case 1:
        return 'Poor';
      default:
        return '';
    }
  }
}
